import React, { useContext } from 'react'
import PropTypes from 'prop-types'

import {
  Button, Form, ModalContext, SectionDivider, SidebarModal,
} from '@domain-group/suit-theme'

import { digObject } from '@domain-group/javascript-utils'

import usePublication, { usePublicationForm } from '@hooks/usePublication'

import ProfileSettings from './ProfileSettings'

const ManagePublicationPrintSpecificationsModal = (props) => {
  const { callbacks, modalKey, showModal } = props
  const { closeModal, updatePublication } = callbacks

  const modalContext = useContext(ModalContext)
  const { modalData } = modalContext

  const modalPayload = digObject(modalData, modalKey, {})
  const { publication } = modalPayload

  const {
    callbacks: { updatePublication: updateFn },
    updating,
  } = usePublication(publication)

  const publicationForm = usePublicationForm(publication)

  const {
    entityState,
    entityState: {
      day_published,
      options,
      page_rows,
      page_columns,
    },
    handlers,
  } = publicationForm

  const {
    image_icc_profile,
    image_icc_profile_rgb,
    pdf_compatibility,
  } = options || {}

  const updatePublicationPayload = {
    callbacks: {
      action: updateFn,
      afterAction: closeModal,
    },
    entityParams: entityState,
    toastText: 'Print Specifications Updated',
  }

  return (
    <SidebarModal callbacks={callbacks} clickSafeZone modalKey={modalKey} showModal={showModal} size="small">
      <SidebarModal.Header callbacks={callbacks} title="Print" titleSecondLine="Specifications" />

      <SidebarModal.Content>
        <Form>
          <Form.Field label="Published On">
            <select name="day_published" value={day_published} {...handlers}>
              <option value="">Please Select...</option>
              <option value="1">Monday</option>
              <option value="2">Tuesday</option>
              <option value="3">Wednesday</option>
              <option value="4">Thursday</option>
              <option value="5">Friday</option>
              <option value="6">Saturday</option>
              <option value="0">Sunday</option>
            </select>
          </Form.Field>

          <Form.Field label="PDF Version" marginTop="large">
            <select name="options.pdf_compatibility" value={pdf_compatibility} {...handlers}>
              <option value="">Default</option>
              <option value="1.3">1.3</option>
              <option value="1.4">1.4</option>
              <option value="1.5">1.5</option>
              <option value="1.6">1.6</option>
              <option value="1.7">1.7 (Default)</option>
            </select>
          </Form.Field>

          <Form.Row marginTop="large">
            <Form.Field label="Page Rows">
              <input
                data-value-type="integer"
                name="page_rows"
                value={page_rows}
                {...handlers}
              />
            </Form.Field>

            <Form.Field label="Page Columns" marginTop={['large', 0]}>
              <input
                data-value-type="integer"
                name="page_columns"
                value={page_columns}
                {...handlers}
              />
            </Form.Field>
          </Form.Row>

          <SectionDivider boxProps={{ marginY: 'large' }}>DESTINATION</SectionDivider>

          <Form.Field label="Image Profile (CMYK)">
            <select name="options.image_icc_profile" value={image_icc_profile} {...handlers}>
              <option value="">Dont Convert (Default)</option>
              <option value="PSO_LWC_Improved_eci_2025.icc">Domain Magazines Feb 2025</option>
              <option value="FairfaxMedia_Newspaper_V3.icc">FairfaxMedia Newspaper</option>
              <option value="ICONSnewspaper26v5.icc">ICONS Newspaper 26v5</option>
              <option value="Newscorp_Gloss.icc">Newscorp Gloss</option>
              <option value="Newscorp_Newsprint.icc">Newscorp Newsprint</option>
              <option value="PSO_LWC_Improved_eci.icc">PSO LWC Improved (ECI)</option>
              <option value="USWebCoatedSWOP.icc">US WebCoated SWOP</option>
            </select>
          </Form.Field>

          <Form.Field label="Image Profile (RGB)" marginTop="large">
            <select name="options.image_icc_profile_rgb" value={image_icc_profile_rgb} {...handlers}>
              <option value="">Dont Convert (Default)</option>
              <option value="AdobeRGB1998.icc">Adobe RGB</option>
              <option value="Newscorp_CustomRGB.icc">Newscorp Custom RGB</option>
            </select>
          </Form.Field>

          <SectionDivider boxProps={{ marginY: 'large' }}>Profile Settings</SectionDivider>

          <ProfileSettings publicationForm={publicationForm} />
        </Form>
      </SidebarModal.Content>

      <SidebarModal.Footer>
        <Button
          buttonStyle="primaryCreate"
          loading={updating}
          onClick={() => updatePublication(updatePublicationPayload)}
          size="large"
        >
          Save Print Specifications
        </Button>
      </SidebarModal.Footer>
    </SidebarModal>
  )
}

ManagePublicationPrintSpecificationsModal.propTypes = {
  callbacks: PropTypes.object.isRequired,
  modalKey: PropTypes.string,
  showModal: PropTypes.bool,
}

ManagePublicationPrintSpecificationsModal.defaultProps = {
  modalKey: 'ManagePublicationPrintSpecificationsModal',
}

const LazyLoadedModal = props => (
  <SidebarModal.RenderController {...props}>
    <ManagePublicationPrintSpecificationsModal {...props} />
  </SidebarModal.RenderController>
)

export default LazyLoadedModal
